import qs from 'qs';
import { FilterOptions } from '~/components/Map/panels/GeocollectDatatablePanel/components/CollectItemFilterPopup/CollectItemFilterPopup';

export type StrapiFilterOperator =
  | '$containsi'
  | '$notContainsi'
  | '$contains'
  | '$notContains'
  | '$eq'
  | '$eqi'
  | '$ne'
  | '$nei'
  | '$startsWithi'
  | '$endsWithi'
  | '$gt'
  | '$gte'
  | '$lt'
  | '$lte'
  | '$between'
  | '$in'
  | '$notIn'
  | '$null'
  | '$notNull';

export type ColumnFilterFieldType = 'text' | 'number' | 'date' | 'boolean' | 'select' | 'user';

export interface ColumnFilterRule {
  columnKey: string;
  label?: string;
  isSystemColumn?: boolean;
  fieldType?: ColumnFilterFieldType;
  operator: StrapiFilterOperator;
  value?: any;
}

export interface ColumnSortRule {
  columnKey: string;
  label?: string;
  isSystemColumn?: boolean;
  direction: 'asc' | 'desc';
}

export const STRAPI_OPERATOR_LABELS: Record<StrapiFilterOperator, string> = {
  $containsi: 'Chứa ($containsi)',
  $notContainsi: 'Không chứa ($notContainsi)',
  $contains: 'Chứa (phân biệt hoa/thường) ($contains)',
  $notContains: 'Không chứa (phân biệt hoa/thường) ($notContains)',
  $eq: 'Bằng ($eq)',
  $eqi: 'Bằng (không phân biệt hoa/thường) ($eqi)',
  $ne: 'Khác ($ne)',
  $nei: 'Khác (không phân biệt hoa/thường) ($nei)',
  $startsWithi: 'Bắt đầu bằng ($startsWithi)',
  $endsWithi: 'Kết thúc bằng ($endsWithi)',
  $gt: 'Lớn hơn ($gt)',
  $gte: 'Lớn hơn hoặc bằng ($gte)',
  $lt: 'Nhỏ hơn ($lt)',
  $lte: 'Nhỏ hơn hoặc bằng ($lte)',
  $between: 'Trong khoảng ($between)',
  $in: 'Trong danh sách ($in)',
  $notIn: 'Ngoài danh sách ($notIn)',
  $null: 'Trống / Null ($null)',
  $notNull: 'Có dữ liệu ($notNull)',
};

export const STRAPI_OPERATOR_SHORT_LABELS: Record<StrapiFilterOperator, string> = {
  $containsi: 'chứa',
  $notContainsi: 'không chứa',
  $contains: 'chứa (exact case)',
  $notContains: 'không chứa (exact case)',
  $eq: '=',
  $eqi: '~=',
  $ne: '!=',
  $nei: '!~=',
  $startsWithi: 'bắt đầu bằng',
  $endsWithi: 'kết thúc bằng',
  $gt: '>',
  $gte: '>=',
  $lt: '<',
  $lte: '<=',
  $between: 'trong khoảng',
  $in: 'thuộc',
  $notIn: 'không thuộc',
  $null: 'is null',
  $notNull: 'not null',
};

/**
 * Check if a ColumnFilterRule has a valid, non-empty condition
 */
export const isColumnFilterRuleActive = (rule?: ColumnFilterRule | null): boolean => {
  if (!rule) return false;
  if (rule.operator === '$null' || rule.operator === '$notNull') {
    return true;
  }
  if (rule.operator === '$between') {
    return (
      Array.isArray(rule.value) &&
      rule.value.length === 2 &&
      rule.value[0] !== null &&
      rule.value[0] !== undefined &&
      rule.value[0] !== '' &&
      rule.value[1] !== null &&
      rule.value[1] !== undefined &&
      rule.value[1] !== ''
    );
  }
  if (rule.operator === '$in' || rule.operator === '$notIn') {
    return Array.isArray(rule.value) && rule.value.length > 0;
  }
  if (typeof rule.value === 'boolean') {
    return true;
  }
  if (typeof rule.value === 'number') {
    return !isNaN(rule.value);
  }
  if (typeof rule.value === 'string') {
    return rule.value.trim() !== '';
  }
  return rule.value !== null && rule.value !== undefined;
};

/**
 * Convert FilterOptions (from modal) + ColumnFilterRules (from column headers) to Strapi filters format
 * Based on Strapi filters documentation: https://docs.strapi.io/dev-docs/api/rest/filters
 */
export const convertFiltersToStrapiQuery = (
  filterOptions: FilterOptions,
  columnFilters?: Record<string, ColumnFilterRule>,
): any => {
  const filters: any = {};

  // 1. Text search - search in properties.name field (Strapi nested approach)
  if (filterOptions.searchText && filterOptions.searchText.trim() !== '') {
    const searchText = filterOptions.searchText.trim();
    filters.properties = {
      ...(filters.properties || {}),
      name: {
        $containsi: searchText,
      },
    };
  }

  // 2. Date range filter (created_at)
  if (filterOptions.dateRange && filterOptions.dateRange.length === 2) {
    const [startDate, endDate] = filterOptions.dateRange;
    filters.created_at = {
      $gte: startDate,
      $lte: endDate,
    };
  }

  // 3. Sync date range filter (sync_at)
  if (filterOptions.syncDateRange && filterOptions.syncDateRange.length === 2) {
    const [startDate, endDate] = filterOptions.syncDateRange;
    filters.sync_at = {
      $gte: startDate,
      $lte: endDate,
    };
  }

  // 4. Creator IDs filter (user relation)
  if (filterOptions.creatorIds && filterOptions.creatorIds.length > 0) {
    filters.user = {
      id: {
        $in: filterOptions.creatorIds,
      },
    };
  }

  // 5. Dynamic form fields from FilterPopup modal (properties.<fieldKey>)
  if (filterOptions.selectedFields) {
    for (const [fieldKey, fieldValue] of Object.entries(filterOptions.selectedFields)) {
      if (fieldValue !== undefined && fieldValue !== null && String(fieldValue).trim() !== '') {
        if (!filters.properties) {
          filters.properties = {};
        }
        filters.properties[fieldKey] = {
          ...(filters.properties[fieldKey] || {}),
          $containsi: String(fieldValue).trim(),
        };
      }
    }
  }

  // 6. Per-column Strapi filter rules from Table Column Headers
  if (columnFilters) {
    for (const rule of Object.values(columnFilters)) {
      if (!isColumnFilterRuleActive(rule)) continue;

      const operatorPayload =
        rule.operator === '$null' || rule.operator === '$notNull'
          ? { [rule.operator]: true }
          : { [rule.operator]: rule.value };

      if (rule.columnKey === 'user' || rule.fieldType === 'user') {
        filters.user = {
          id: {
            ...(filters.user?.id || {}),
            ...operatorPayload,
          },
        };
      } else if (rule.isSystemColumn) {
        filters[rule.columnKey] = {
          ...(filters[rule.columnKey] || {}),
          ...operatorPayload,
        };
      } else {
        if (!filters.properties) {
          filters.properties = {};
        }
        filters.properties[rule.columnKey] = {
          ...(filters.properties[rule.columnKey] || {}),
          ...operatorPayload,
        };
      }
    }
  }

  return filters;
};

/**
 * Convert ColumnSortRule to Strapi sort string (e.g. "properties.chieu_cao:asc" or "sync_at:desc")
 */
export const convertSortToStrapiQuery = (
  columnSort?: ColumnSortRule | null,
): string | undefined => {
  if (!columnSort) return undefined;
  if (columnSort.columnKey === 'user') {
    return `user_id:${columnSort.direction}`;
  }
  if (columnSort.isSystemColumn) {
    return `${columnSort.columnKey}:${columnSort.direction}`;
  }
  return `properties.${columnSort.columnKey}:${columnSort.direction}`;
};

/**
 * Format Strapi filters & sort as a readable query string (e.g. filters[properties][name][$containsi]=abc&sort=sync_at:desc)
 */
export const formatStrapiQueryPreview = (filters: any, sort?: string): string => {
  const queryObj: Record<string, any> = {};
  if (filters && Object.keys(filters).length > 0) {
    queryObj.filters = filters;
  }
  if (sort) {
    queryObj.sort = sort;
  }
  if (Object.keys(queryObj).length === 0) return '';
  return qs.stringify(queryObj, { encodeValuesOnly: true });
};

/**
 * Convert FilterOptions to complete query string for Strapi API
 */
export const buildStrapiQueryString = (
  filterOptions: FilterOptions,
  pagination?: { page: number; pageSize: number },
  populate?: string[],
  columnFilters?: Record<string, ColumnFilterRule>,
  columnSort?: ColumnSortRule | null,
): string => {
  const queryObject: any = {};

  // Add filters
  const filters = convertFiltersToStrapiQuery(filterOptions, columnFilters);
  if (Object.keys(filters).length > 0) {
    queryObject.filters = filters;
  }

  // Add sort
  const sort = convertSortToStrapiQuery(columnSort);
  if (sort) {
    queryObject.sort = sort;
  }

  // Add pagination
  if (pagination) {
    queryObject.pagination = {
      page: pagination.page,
      pageSize: pagination.pageSize,
    };
  }

  // Add populate
  if (populate && populate.length > 0) {
    queryObject.populate = populate;
  }

  return qs.stringify(queryObject, {
    encodeValuesOnly: true, // prettify URL
  });
};
